import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../store/selectors';
import { useCurrentMarketplaceParty } from '../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { FetchEngagementConferences } from '../../slices/marketplace/meetings/thunks/fetch-engagement-conferences.thunk';
import { EngagementMeeting } from '../../slices/marketplace/meetings/models/get-engagement-conference';
import './MeetingLinkRedirect.scss';
import Loader from 'react-loaders';
import { SellerMarketplaceTab } from '../../slices/marketplace/timeline/models/enums/seller-marketplace-tab.enum';
import { BuyerOppMarketplaceTab } from '../../slices/marketplace/timeline/models/enums/buyer-opportunity-details-tab.enum';
import { MarketplaceCommsHubTab } from '../AdminMarketplace/MarketplaceTabs/MarketplaceCommsHub/MarketplaceCommsHubTabs';
import { AdminMarketplaceTab } from '../../slices/marketplace/timeline/models/enums/admin-marketplace-tab.enum';
import { BuyerMarketplaceTab } from '../../slices/marketplace/timeline/models/enums/buyer-marketplace-tab.enum';

const MeetingLinkRedirect = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch() as any;
    const [isLoading, setIsLoading] = useState(true);
    const axios = useStateSelector((state) => state.core.axios);

    const engagementId = Number(searchParams.get('engagementId')) || 0;
    const meetingId = Number(searchParams.get('meetingId')) || 0;

    const summary = useStateSelector((state) => state.userSummary.summary);
    const party = useCurrentMarketplaceParty(engagementId);

    const renderLoader = () => {
        return (
            <div className="meeting-link-redirect">
                <div className="loader-container">
                    <div className="loader-container-inner">
                        <div className="text-center">
                            <Loader type="ball-scale-multiple" active={true} />
                        </div>
                        <h6 className="mt-5">Loading your request...</h6>
                    </div>
                </div>
            </div>
        );
    };

    const navigateToEngagementMeeting = (
        foundMeeting: EngagementMeeting | null
    ) => {
        if (!foundMeeting) {
            navigate('/user-home');
            return;
        }

        sessionStorage.setItem('scrollToMeetingId', meetingId.toString());

        if (party === TimelineParty.Buyer) {
            navigate(
                `/opportunities?opportunityId=${foundMeeting.engagementId}&tab=${BuyerOppMarketplaceTab.CommsHub}&subtab=${MarketplaceCommsHubTab.Meetings}`
            );
        } else if (
            party === TimelineParty.Seller ||
            party === TimelineParty.Broker
        ) {
            navigate(
                `/marketplace/engagement/${foundMeeting.engagementId}?tab=${SellerMarketplaceTab.CommsHub}&subtab=${MarketplaceCommsHubTab.Meetings}`
            );
        } else if (party === TimelineParty.VetValue) {
            navigate(
                `/marketplace/admin/engagement/${foundMeeting.engagementId}?tab=${SellerMarketplaceTab.CommsHub}&subtab=${MarketplaceCommsHubTab.Meetings}`
            );
        } else {
            navigate('/user-home');
        }
    };

    const navigateToMarcketplaceMeeting = (
        foundMeeting: EngagementMeeting | null
    ) => {
        if (!foundMeeting) {
            navigate('/user-home');
            return;
        }

        sessionStorage.setItem('scrollToMeetingId', meetingId.toString());

        if (party === TimelineParty.Buyer) {
            navigate(
                `/opportunities?tab=${BuyerMarketplaceTab.Meetings}`
            );
        } else if (party === TimelineParty.VetValue) {
            navigate(
                `/marketplace/admin?tab=${AdminMarketplaceTab.SupportMeetings}`
            );
        } else {
            navigate('/user-home');
        }
    };

    useEffect(() => {
        const handleRedirect = async () => {
            if (!summary) {
                return;
            }
            if (engagementId === 0) {
                try {
                    const response = await axios.get('/api/Marketplace/MarketplaceMeetings');
                    if (response.status === 200) {
                        const marketplaceMeetings = (response.data as EngagementMeeting[]) || [];
                        const foundMeeting = marketplaceMeetings.find(
                            (m) => m.id === meetingId
                        );

                        if (!foundMeeting) {
                            navigate('/user-home');
                            return;
                        }

                        if (!foundMeeting.engagementId || foundMeeting.engagementId === 0) {
                            navigateToMarcketplaceMeeting(foundMeeting);
                        } else {
                            navigateToEngagementMeeting(foundMeeting);
                        }
                    } else {
                        navigate('/user-home');
                    }
                } catch (error) {
                    navigate('/user-home');
                }
                return;
            }

            if (engagementId > 0) {
                try {
                    const result = await dispatch(
                        FetchEngagementConferences(engagementId)
                    );
                    const engagementMeetings =
                        (result.payload as EngagementMeeting[]) || [];
                    const foundMeeting = engagementMeetings?.find(
                        (m) => m.id === meetingId
                    );

                    if (!foundMeeting) {
                        navigate('/user-home');
                        return;
                    }

                    navigateToEngagementMeeting(foundMeeting);
                } catch (error) {
                    navigate('/user-home');
                }
            } else {
                navigate('/user-home');
            }
        };

        handleRedirect().finally(() => {
            setIsLoading(false);
        });
    }, [engagementId, meetingId, summary, party]);

    if (isLoading) {
        return renderLoader();
    }

    return renderLoader();
};

export default MeetingLinkRedirect;
